import React, {useEffect, useRef, useState} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
    FaUser,
    FaBuilding,
    FaClock,
    FaPhone,
    FaEnvelope,
    FaBirthdayCake,
    FaSchool,
    FaIdCard,
    FaCalendarAlt,
    FaFlag,
    FaBalanceScale,
    FaBriefcase,
    FaArrowLeft,
    FaDownload,
    FaFile, FaCalendar
} from 'react-icons/fa';
import './FormSummary.css';
import '../error.css'
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useForm} from "react-hook-form";
import {getEmployeeRequest} from "../redux/actions/employeeActions";
import {getEmploymentsRequest} from "../redux/actions/employmentActions";
import {acceptChangeContractRequest, updateContractRequest} from "../redux/actions/contractActions";
import {downloadFile as apiDownloadFile} from "../redux/utils/api";

const FormSummary = ({ contract, previousStep, submitButtonText }) => {
    const { handleSubmit } = useForm({});
    const dispatch = useDispatch();
    const employee = useSelector(state => state.employee.employee);
    const isLoadingEmployee = useSelector(state => state.employee.loading.getEmployee);
    const isUpdatingContract = useSelector(state => state.contract.loading.updateContract);
    const employments = useSelector(state => state.employment.employments);
    const isLoadingEmployments = useSelector(state => state.employment.loading.getEmployments);
    const userGroups = useSelector(state => state.authentication.userGroups);
    const selectedRole = useSelector(state => state.authentication.selectedRole);
    const authToken = useSelector(state => state.authentication.authToken);
    const navigate = useNavigate();

    const isInstitute = userGroups.includes('institute') || userGroups.includes('assistant');
    const isHR = userGroups.includes('humanressource');
    const isRectorate = userGroups.includes('rectorate') || userGroups.includes('rectorate-assistant');

    const [signature, setSignature] = useState(null);
    const [showSignature, setShowSignature] = useState(false);
    const sigPad = useRef({});

    const handleAcceptChanges = () => {
        dispatch(acceptChangeContractRequest({ contractID: contract.ID, accept: true }));
    };

    const handleRefuseChanges = () => {
        dispatch(acceptChangeContractRequest({ contractID: contract.ID, accept: false }));
    };

    useEffect(() => {
        if (employee.ID !== contract.EmployeeID && !isLoadingEmployee) {
            dispatch(getEmployeeRequest({contractID: contract.ID}));
        }
        if (employee.ID === contract.EmployeeID && employments.length === 0 && !isLoadingEmployments) {
            dispatch(getEmploymentsRequest({ contractID: contract.ID, employeeID: employee.ID }));
        }
    }, [dispatch, employee.ID, contract, employments, isLoadingEmployee, isLoadingEmployments]);

    useEffect(() => {
        if (contract.Signature) {
            setShowSignature(true);
        }
    }, [contract.Signature]);

    // Funktion, um die Signatur zu speichern
    const saveSignature = () => {
        setSignature(sigPad.current.getTrimmedCanvas().toDataURL('image/png'));
    };

    const clearSignature = () => {
        sigPad.current.clear();
        setSignature(null);
    };

    const onSubmit = async () => {
        if (!isUpdatingContract) {
            const updatedContract = {...contract, Signature: signature, IsFinalStep: 1};
            updatedContract.selectedRole = selectedRole;
            dispatch(updateContractRequest(updatedContract));
            if (isInstitute) {
                navigate("/mdb/institute/overview");
            } else if (isHR) {
                navigate("/mdb/hr/overview");
            } else if (isRectorate) {
                navigate("/mdb/rectorate/overview");
            } else {
                navigate("/mdb/staff");
            }
        }
    }

    const downloadFile = async (token, index) => {
        const fileToDownload = employee.EmployeeFiles[index];
        let apiUrl;
        let fileName;

        apiUrl = `${process.env.REACT_APP_API_URL}/employee-api/getEmployeeFile/${fileToDownload.ID}`;
        fileName = `${fileToDownload.File.Name}`;

        const response = await apiDownloadFile(apiUrl);

        if (!response.ok) {
            return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="form-summary-container">
            {contract.CopyChanged === 1 && (
                <div className="alert alert-warning text-center" style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
                    <strong>Achtung!</strong> Dieser Vertrag wurde verändert (Kopie / Änderung).
                    <div style={{ marginTop: '1rem' }}>
                        <button className="btn btn-success me-3" onClick={handleAcceptChanges}>
                            Änderung bestätigen
                        </button>
                        <button className="btn btn-danger" onClick={handleRefuseChanges}>
                            Änderung ablehnen
                        </button>
                    </div>
                </div>
            )}
            <h3 className="summary-title">Übersicht</h3>
            <div className="summary-item">
                <FaEnvelope color="#CC1617" /> <strong>Email:</strong> {employee.InitialEmail}
            </div>
            {employee.Title && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Titel:</strong> {employee.Title}
                </div>
            )}
            <div className="summary-item">
                <FaUser color="#CC1617"/> <strong>Name:</strong> {employee.GivenName} {employee.FamilyName}
            </div>
            {employee.TitleAfter && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Titel danach:</strong> {employee.TitleAfter}
                </div>
            )}
            {employee.TitleOfficial && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Amtstitel (Berufstitel):</strong> {employee.TitleOfficial}
                </div>
            )}
            {employee.Birthday && (
                <div className="summary-item">
                    <FaBirthdayCake color="#CC1617"/> <strong>Geburtsdatum:</strong> {employee.Birthday}
                </div>
            )}
            {employee.SocialSecurityNumber && (
                <div className="summary-item">
                    <FaEnvelope color="#CC1617"/> <strong>Sozialversicherungsnummer:</strong> {employee.SocialSecurityNumber}
                </div>
            )}
            {employee.PhoneNumber && (
                <div className="summary-item">
                    <FaPhone color="#CC1617"/> <strong>Telefonnummer:</strong> {employee.PhoneNumber}
                </div>
            )}
            {employee.Nationality && (
                <div className="summary-item">
                    <FaFlag color="#CC1617"/> <strong>Staatsangehörigkeit:</strong> {employee.Nationality}
                </div>
            )}
            {employee.EmployeeFiles && (
                <div className="summary-item">
                    <FaFile color="#CC1617"/> <strong>Dateien:</strong>
                    {(employee.EmployeeFiles || []).map((file, index) => (
                        <div key={index}>
                            {file.File.Name}
                            <span role="button" onClick={() => downloadFile(authToken, index)} tabIndex="0"
                                  style={{color: "blue"}}><FaDownload/>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <hr/>
            {employments.length > 0 && contract.SchoolIdentificationNumber && (
                <div className="summary-item">
                    <FaSchool color="#CC1617"/> <strong>Schulkennzahl:</strong> {contract.SchoolIdentificationNumber}
                </div>
            )}
            {employments.length > 0 && contract.Start && (
                <div className="summary-item">
                    <FaCalendar color="#CC1617"/> <strong>Ab:</strong> {contract.Start}
                </div>
            )}
            {employments.length > 0 && contract.Start && (
                <div className="summary-item">
                    <FaCalendar color="#CC1617"/> <strong>Bis:</strong> {contract.End}
                </div>
            )}
            {employments.length > 0 && contract.SchoolEmployeeNumber && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Personalnummer:</strong> {contract.SchoolEmployeeNumber}
                </div>
            )}
            {employments.length > 0 && contract.SchoolEntryDate && (
                <div className="summary-item">
                    <FaCalendarAlt color="#CC1617"/> <strong>Eintritt (Schule):</strong> {contract.SchoolEntryDate}
                </div>
            )}
            {employments.length > 0 && contract.NewOrExtension && (
                <div className="summary-item">
                    <FaCalendarAlt color="#CC1617"/>
                    <strong>Neu/Verlängerung:</strong> {contract.NewOrExtension === "NEW" ? "Neu" : "Verlängerung"}
                </div>
            )}
            {employments.length > 0 && contract.StateOrFederal && (
                <div className="summary-item">
                    <FaBriefcase color="#CC1617"/>
                    <strong>Vertragspartner:</strong> {contract.StateOrFederal !== "NONE" && contract.StateOrFederal === "STATE" ? "Bund" : "Land"}
                </div>
            )}
            {employments.length > 0 && contract.ContractOrCivil && (
                <div className="summary-item">
                    <FaBriefcase color="#CC1617"/>
                    <strong>Beschäftigungsverhältnis:</strong> {contract.ContractOrCivil !== "NONE" && contract.ContractOrCivil === "CONTRACT" ? "Vertraglich" : "Pragmatisiert"}
                </div>
            )}
            {employments.length > 0 && contract.FixedOrPermanent && (
                <div className="summary-item">
                    <FaBalanceScale color="#CC1617"/>
                    <strong>Vertragstyp:</strong> {contract.FixedOrPermanent === "FIXED" ? "Befristet" : "Unbefristet"}
                </div>
            )}
            {employments.length > 0 && contract.ClassificationType && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Einstufung:</strong> {contract.ClassificationType}
                </div>
            )}
            {employments?.map((employment, index) => (
                <div key={index} className="summary-item">
                    <span><FaBuilding color="#CC1617"/> <strong>Institut ({index + 1}):</strong> {employment.Institute}</span>
                    <span><FaIdCard color="#CC1617"/> <strong>Jobtitel:</strong> {employment.JobTitle}</span>
                    {employment.NumOfUnits &&
                        <span><FaClock color="#CC1617"/> <strong>Planend:</strong> {employment.NumOfUnits} {employment.UnitType}</span>}
                    {employment.NumOfUnitsTeaching > 0 &&
                        <span><FaClock color="#CC1617"/> <strong>Lehrend:</strong> {employment.NumOfUnitsTeaching} {employment.UnitTypeTeaching}</span>}
                </div>
            ))}
            {employments.length > 0 && contract.TotalUnits !== "" && (
                <div className="summary-item">
                    <FaIdCard color="#CC1617"/> <strong>Gesamtanzahl an der WE an der PH:</strong> {contract.TotalUnits}
                </div>
            )}
            {showSignature && (
                <div className="row justify-content-center">
                    <div className="col-6 d-flex flex-column align-items-center">
                        {contract.Signature && (
                            <div className="signature-preview text-center">
                                <div className="signature-title">Gespeicherte Signatur</div>
                                <img src={contract.Signature} alt="Signatur-Vorschau"
                                     style={{width: '100%', maxHeight: '300px'}}/>
                            </div>
                        )}
                        {!isInstitute && contract.Status !== "RECTORATE" && submitButtonText !== "" && (
                            <div>
                                <button type="button" className="btn-secondary" onClick={() => setShowSignature(false)}>Signatur
                                    löschen
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {!isInstitute && !showSignature && contract.Status !== "RECTORATE" && submitButtonText !== "" && (
                <div className="row">
                    <div className="col text-center">
                        <div className="signature-container">
                        <div className="signature-title">Unterschrift <b>(optional)</b></div>
                            <SignatureCanvas
                                ref={sigPad}
                                penColor='black'
                                canvasProps={{
                                    width: 800,
                                    height: 300,
                                    className: 'sigCanvas'
                                }}
                                onEnd={saveSignature}
                            />
                            <div>
                                <button type="button" className="btn-secondary" onClick={clearSignature}>Signatur
                                    löschen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="contract-form">
                <div className="form-navigation-buttons">
                    {previousStep && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={previousStep}
                        >
                            <FaArrowLeft/> Zurück
                        </button>
                    )}
                    {submitButtonText && (
                        <button type="submit" className="btn btn-primary">{submitButtonText}</button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default FormSummary;
